// Search bar component with voice activation indicator
// Premium micro-interactions

import { useState, useRef, useCallback, type KeyboardEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import type { VoiceState } from '@/types';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => void;
  voiceState?: VoiceState;
  onMicClick?: () => void;
  onMicDown?: () => void;  // For PTT hold-to-talk
  onMicUp?: () => void;    // For PTT release-to-stop
  isPttMode?: boolean;
  placeholder?: string;
  className?: string;
}

export function SearchBar({
  value,
  onChange,
  onSearch,
  voiceState = 'idle',
  onMicClick,
  onMicDown,
  onMicUp,
  isPttMode = false,
  placeholder = '"Hey Ventura" or type to search...',
  className,
}: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && value.trim()) {
        onSearch(value.trim());
      }
    },
    [value, onSearch]
  );

  const handleClear = useCallback(() => {
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  const isListening =
    voiceState === 'listening-wake' || voiceState === 'listening-command';
  const isProcessing = voiceState === 'processing';

  return (
    <div
      className={clsx(
        'relative w-full transition-all duration-300',
        className
      )}
    >
      <div
        className={clsx(
          'flex items-center gap-4 px-6 py-4 rounded-2xl border-2 transition-all duration-300',
          'bg-workshop-surface',
          isFocused
            ? 'border-workshop-accent shadow-lg'
            : 'border-workshop-border',
          isListening && 'animate-pulse-glow border-workshop-accent'
        )}
      >
        {/* Voice indicator - click for full voice, hold for PTT */}
        <button
          type="button"
          onClick={isPttMode ? undefined : onMicClick}
          onPointerDown={isPttMode ? (e) => {
            e.preventDefault();
            onMicDown?.();
          } : undefined}
          onPointerUp={isPttMode ? (e) => {
            e.preventDefault();
            onMicUp?.();
          } : undefined}
          onPointerLeave={isPttMode ? onMicUp : undefined}
          onPointerCancel={isPttMode ? onMicUp : undefined}
          className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-workshop-accent rounded-full active:scale-95 transition-transform"
          aria-label={isPttMode ? 'Hold to talk' : (voiceState === 'idle' ? 'Start voice recognition' : 'Voice recognition active')}
        >
          <VoiceIndicator state={voiceState} />
        </button>

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={clsx(
            'flex-1 bg-transparent text-workshop text-white',
            'placeholder:text-gray-500 outline-none',
            'min-h-touch'
          )}
        />

        {/* Clear button - animated entrance/exit */}
        <AnimatePresence>
          {value && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleClear}
              className={clsx(
                'flex-shrink-0 p-3 rounded-xl transition-colors',
                'text-gray-400 hover:text-white hover:bg-workshop-border',
                'min-w-touch min-h-touch flex items-center justify-center'
              )}
              aria-label="Clear search"
            >
              <ClearIcon />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Search button */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => value.trim() && onSearch(value.trim())}
          disabled={!value.trim() || isProcessing}
          className={clsx(
            'flex-shrink-0 px-6 py-3 rounded-xl font-semibold transition-all',
            'min-h-touch flex items-center justify-center gap-2',
            value.trim()
              ? 'bg-workshop-accent text-white hover:bg-workshop-accent-hover'
              : 'bg-workshop-border text-gray-500 cursor-not-allowed'
          )}
        >
          <SearchIcon />
          <span className="text-workshop-sm">Search</span>
        </motion.button>
      </div>
    </div>
  );
}

function VoiceIndicator({ state }: { state: VoiceState }) {
  const isActive =
    state === 'listening-wake' ||
    state === 'listening-command' ||
    state === 'processing';

  return (
    <div
      className={clsx(
        'w-12 h-12 rounded-full flex items-center justify-center transition-all',
        state === 'listening-command' && 'bg-workshop-accent animate-pulse',
        state === 'listening-wake' && 'bg-workshop-border',
        state === 'processing' && 'bg-workshop-warning',
        state === 'error' && 'bg-workshop-error',
        state === 'idle' && 'bg-workshop-border'
      )}
    >
      <MicrophoneIcon isActive={isActive} />
    </div>
  );
}

function MicrophoneIcon({ isActive }: { isActive: boolean }) {
  return (
    <svg
      className={clsx('w-6 h-6', isActive ? 'text-white' : 'text-gray-400')}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
